import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Chip, Dialog, InputBase, LinearProgress, List, ListItemButton, ListItemText, ListSubheader, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/stores';
import { USER_SETTINGS_KEYS } from 'src/constants/userSetting';
import { filterCommands, flattenSearchResults, matchRange, rankItems, readRecents, rememberRecent, shouldSearch, toCommands, toKnowledgeCommands, withRecentsSection, groupBySection, inDisplayOrder, truncatedSections } from './commands';
import { currentRole, isLivyAdmin, isLivySuperAdmin } from './access';
import { askLivy, helpLivy } from './LivyGreeting';
import { isAppfolioFrom, reportsDisabledFor } from 'src/pages/ReportingPage/tabsFor';
import { parseJwt } from 'src/core/services/jwt';
import { livyConfig, livyFetch } from 'src/lib/livyTransport';
import { freshToken } from 'src/lib/livyApi';
import { AUTH_TOKEN_KEYS } from 'src/constants/authToken';
import { scopeShortcut } from 'src/lib/platformKeys';
import { LivyTheme } from './LivyTheme';

/** Event the header search field dispatches; the palette owns its open state, not the header. */
export const PALETTE_EVENT = 'livy:palette';

/**
 * Cmd-K / Ctrl-K anywhere in the app, plus the header search field.
 *
 * `enabled` is the same gate the launcher applies. Binding regardless meant preventDefault ran for
 * everyone, so Cmd-K stopped reaching the browser for every user who has no palette to open. The
 * launcher mounts app-wide and hooks run before its early return, so dropping the parameter does
 * not just widen the binding, it swallows the shortcut for everyone without Livy.
 */
export function useCommandPalette() {
  let enabled = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    open = _useState2[0],
    setOpen = _useState2[1];
  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = e => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(v => !v);
      }
    };
    const onRequest = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener(PALETTE_EVENT, onRequest);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(PALETTE_EVENT, onRequest);
    };
  }, [enabled]);
  return {
    open,
    setOpen
  };
}
const SCOPE_KEY = 'livy_search_scope';

/**
 * Which companies a search covers. Super admins only, because widening it reads past the tenant fence.
 *
 * Remembered per company, so switching the company you are impersonating starts you back inside
 * its fence rather than silently leaving you reading the whole platform. Anything unreadable, or
 * remembered against another company, falls back to the default for the caller and never wider.
 *
 * The default is company scope, except for staff who are not impersonating anyone: they are not
 * inside a company, so company scope has nothing it could return and every search would come back
 * empty. Platform is then the only scope that can answer, and it is one they are allowed to use.
 *
 * canWiden defaults to false, so a caller that does not say the role allows the wide scope gets
 * the narrow one whatever is remembered.
 */
export function readScope(companyId) {
  let canWiden = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  // Fails closed on its own rather than trusting every call site to have checked the role first.
  // The proxy is what actually refuses a non-staff caller, but a helper that can hand back the
  // wide scope without being asked whether the caller may have it is one waiting to be misused.
  if (!canWiden) return 'company';
  const fallback = companyId ? 'company' : 'platform';
  try {
    var _localStorage$getItem, _saved$companyId;
    const saved = JSON.parse((_localStorage$getItem = localStorage.getItem(SCOPE_KEY)) !== null && _localStorage$getItem !== void 0 ? _localStorage$getItem : 'null');
    if (((_saved$companyId = saved === null || saved === void 0 ? void 0 : saved.companyId) !== null && _saved$companyId !== void 0 ? _saved$companyId : null) !== companyId) return fallback;
    return (saved === null || saved === void 0 ? void 0 : saved.scope) === 'platform' ? 'platform' : fallback;
  } catch (_unused) {
    return fallback;
  }
}
function rememberScope(companyId, scope) {
  try {
    localStorage.setItem(SCOPE_KEY, JSON.stringify({
      companyId,
      scope
    }));
  } catch (_unused2) {
    /* a browser that refuses storage still gets a working toggle, just not a remembered one */
  }
}

/**
 * The company the caller is acting in, read from the token rather than from a settings key.
 *
 * It is the same claim the proxy fences on, so the palette's idea of which company it is in can
 * never drift from the server's - and USER_SETTINGS_KEYS.companyId is not written by the
 * impersonation flow at all, so keying on it meant the scope below never reset.
 */
function currentCompanyId() {
  var _claims$customCompan;
  const token = localStorage.getItem(AUTH_TOKEN_KEYS.accessToken);
  const claims = token ? parseJwt(token) : null;
  // Cognito emits the literal string "null" when nobody is being impersonated, not an absent
  // claim. Normalised here, once, so nothing downstream treats "null" as a company it can search.
  const claimed = String((_claims$customCompan = claims === null || claims === void 0 ? void 0 : claims['custom:companyid']) !== null && _claims$customCompan !== void 0 ? _claims$customCompan : '').trim();
  return claimed && claimed !== 'null' && claimed !== 'undefined' ? claimed : null;
}

/** Never past the end, never below zero — an out-of-range cursor makes Enter do nothing. */
const clampCursor = (next, length) => Math.max(0, Math.min(next, length - 1));

/** Emphasises the typed substring, so a long list shows why each row is in it. */
const Highlighted = _ref => {
  let label = _ref.label,
    query = _ref.query;
  const range = matchRange(label, query);
  if (!range) return /*#__PURE__*/React.createElement(React.Fragment, null, label);
  const _range = _slicedToArray(range, 2),
    from = _range[0],
    to = _range[1];
  return /*#__PURE__*/React.createElement(React.Fragment, null, label.slice(0, from), /*#__PURE__*/React.createElement(Box, {
    component: "mark",
    sx: {
      bgcolor: 'transparent',
      color: 'inherit',
      fontWeight: 700
    }
  }, label.slice(from, to)), label.slice(to));
};
const LivyCommandPaletteBody = observer(_ref2 => {
  var _store$account$featur, _store$account$featur2, _store$company$compan, _store$company$compan2, _store$company$compan3, _store$account$featur3, _store$account$featur4, _store$homeDashboardS, _store$company$compan4, _currentCompany$compa, _store$company$compan5, _currentCompany$compa2;
  let open = _ref2.open,
    onClose = _ref2.onClose;
  const navigate = useNavigate();
  const _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    query = _useState4[0],
    setQuery = _useState4[1];
  const _useState5 = useState(0),
    _useState6 = _slicedToArray(_useState5, 2),
    cursor = _useState6[0],
    setCursor = _useState6[1];
  const _useState7 = useState([]),
    _useState8 = _slicedToArray(_useState7, 2),
    hits = _useState8[0],
    setHits = _useState8[1];
  const _useState9 = useState([]),
    _useState0 = _slicedToArray(_useState9, 2),
    articles = _useState0[0],
    setArticles = _useState0[1];
  // Which sections hold more matches than were returned, so a count can read as a floor.
  const _useState1 = useState([]),
    _useState10 = _slicedToArray(_useState1, 2),
    cutShort = _useState10[0],
    setCutShort = _useState10[1];
  const _useState11 = useState(false),
    _useState12 = _slicedToArray(_useState11, 2),
    searching = _useState12[0],
    setSearching = _useState12[1];
  const _useState13 = useState(false),
    _useState14 = _slicedToArray(_useState13, 2),
    failed = _useState14[0],
    setFailed = _useState14[1];
  const _useState15 = useState(0),
    _useState16 = _slicedToArray(_useState15, 2),
    attempt = _useState16[0],
    setAttempt = _useState16[1];
  // Reactive, not read inside the memo: the component stays mounted while the dialog is closed, so
  // a selection made on an empty query would otherwise not show up the next time it is opened.
  const _useState17 = useState(readRecents),
    _useState18 = _slicedToArray(_useState17, 2),
    recents = _useState18[0],
    setRecents = _useState18[1];
  const listRef = useRef(null);
  // The token's role: the palette mounts right after login, before the settings mirror is written.
  const role = currentRole();

  // Presentation only. The proxy refuses platform scope to anyone whose verified token is not a
  // super admin, so hiding the control is about not offering a door that leads nowhere.
  const canWiden = isLivySuperAdmin(role);
  const companyId = currentCompanyId();
  // Other staff search inside the company they impersonate; without one there is nothing to search.
  const needsCompany = isLivyAdmin(role) && !canWiden && !companyId;
  const companyName = localStorage.getItem(USER_SETTINGS_KEYS.companyName) || 'This company';
  const _useState19 = useState(() => canWiden ? readScope(companyId, canWiden) : 'company'),
    _useState20 = _slicedToArray(_useState19, 2),
    scope = _useState20[0],
    setScope = _useState20[1];
  // What the server said it actually did, which is not always what was asked: a new bundle against
  // an older Lambda gets company scope, and labelling that "all companies" would be a lie.
  const _useState21 = useState(scope),
    _useState22 = _slicedToArray(_useState21, 2),
    appliedScope = _useState22[0],
    setAppliedScope = _useState22[1];

  // Staff who are not impersonating have no company to narrow to, so only the wide scope is
  // offered - a control whose other half can only ever return nothing is a dead end, not a choice.
  const scopeOptions = companyId ? [['company', companyName], ['platform', 'All companies']] : [['platform', 'All companies']];
  const canToggle = canWiden && scopeOptions.length > 1;
  const chooseScope = useCallback(next => {
    setScope(next);
    rememberScope(companyId, next);
  }, [companyId]);

  // The palette body stays mounted between openings, so the initialiser above runs once for the
  // life of the session. Without this, choosing All companies and then switching the company you
  // are impersonating left the next search still reading every tenant.
  useEffect(() => {
    setScope(canWiden ? readScope(companyId, canWiden) : 'company');
  }, [companyId, canWiden]);

  // The same flags the sidebar checks, so the palette cannot open a destination it hides.
  const store = useStore();
  const newPaymentsAndSubscriptions = Boolean((_store$account$featur = store.account.featureFlagsStore) === null || _store$account$featur === void 0 ? void 0 : _store$account$featur.getIsNewPaymentsAndSubscriptions);
  const stripeInvoicing = Boolean((_store$account$featur2 = store.account.featureFlagsStore) === null || _store$account$featur2 === void 0 ? void 0 : _store$account$featur2.getIsStripeInvoicingApiEnabled);
  const canAddProperty = !((_store$company$compan = store.company.companyFeatureStore) !== null && _store$company$compan !== void 0 && _store$company$compan.isAppfolio);
  const notDemoMode = localStorage.getItem(AUTH_TOKEN_KEYS.demoPropertyMode) !== 'Demo';
  const canUseFeeStructure = !((_store$company$compan2 = store.company.companyFeatureStore) !== null && _store$company$compan2 !== void 0 && _store$company$compan2.disableFeeAssessmentMethod) && !((_store$company$compan3 = store.company.companyFeatureStore) !== null && _store$company$compan3 !== void 0 && _store$company$compan3.isAppfolio);
  const billsDashboard = Boolean((_store$account$featur3 = store.account.featureFlagsStore) === null || _store$account$featur3 === void 0 ? void 0 : _store$account$featur3.getIsBillsDashboardEnabled);
  const utilitySetups = notDemoMode && Boolean((_store$account$featur4 = store.account.featureFlagsStore) === null || _store$account$featur4 === void 0 ? void 0 : _store$account$featur4.getIsBillingOptionsEnabled);
  // Same decision as the sidebar's Reports entry.
  const currentCompany = (_store$homeDashboardS = store.homeDashboardStore) === null || _store$homeDashboardS === void 0 ? void 0 : _store$homeDashboardS.getCurrentCompany;
  const reports = !reportsDisabledFor((_store$company$compan4 = store.company.companyFeatureStore) === null || _store$company$compan4 === void 0 ? void 0 : _store$company$compan4.disableReports, currentCompany === null || currentCompany === void 0 || (_currentCompany$compa = currentCompany.companyFeatureSet) === null || _currentCompany$compa === void 0 ? void 0 : _currentCompany$compa.disableReports, isAppfolioFrom((_store$company$compan5 = store.company.companyFeatureStore) === null || _store$company$compan5 === void 0 ? void 0 : _store$company$compan5.isAppfolio, currentCompany === null || currentCompany === void 0 || (_currentCompany$compa2 = currentCompany.companyFeatureSet) === null || _currentCompany$compa2 === void 0 ? void 0 : _currentCompany$compa2.isAppfolio));
  const commands = useMemo(() => filterCommands(query, role, {
    newPaymentsAndSubscriptions,
    stripeInvoicing,
    canAddProperty,
    notDemoMode,
    canUseFeeStructure,
    billsDashboard,
    reports,
    utilitySetups
  }), [query, role, newPaymentsAndSubscriptions, stripeInvoicing, canAddProperty, notDemoMode, canUseFeeStructure, billsDashboard, reports, utilitySetups]);

  // Ranked, then grouped, then flattened: the keyboard walks exactly what is on screen.
  const items = useMemo(() => {
    const ranked = rankItems([...commands, ...toCommands(hits), ...toKnowledgeCommands(articles, isLivyAdmin(role))], query);
    return inDisplayOrder(query.trim() ? ranked : withRecentsSection(ranked, recents));
  }, [commands, hits, articles, query, recents, role]);

  // Sections render as headings while the flat order still drives the keyboard, so ArrowDown never
  // jumps between panes and Enter always opens the highlighted row.
  const sections = useMemo(() => groupBySection(items), [items]);
  useEffect(() => {
    setCursor(0);
  }, [query]);

  // Another surface may have navigated since this palette was last opened, and the body stays
  // mounted while the dialog is closed — so the cursor has to start over too, or reopening
  // highlights whatever now sits at the old index.
  useEffect(() => {
    if (!open) return;
    setRecents(readRecents());
    setCursor(0);
  }, [open]);

  // The list can also shrink without the query changing — a retry clears results, a search
  // returns fewer rows — which would leave the cursor past the end and Enter doing nothing.
  useEffect(() => {
    setCursor(c => clampCursor(c, items.length));
  }, [items.length]);

  // Debounced so a fast typist issues one query, and aborted so a stale response cannot overwrite
  // the results for what is now on screen.
  useEffect(() => {
    const config = livyConfig();
    if (!open || !config || !shouldSearch(query)) {
      setHits([]);
      setArticles([]);
      setSearching(false);
      setFailed(false);
      return undefined;
    }
    const controller = new AbortController();
    // Cleared as the new search starts, not when it returns: otherwise Enter during the debounce
    // opens the previous query's first result, which is a different property or resident.
    setHits([]);
    setArticles([]);
    setCutShort([]);
    setSearching(true);
    setFailed(false);

    // freshToken, not the raw store: livyFetch signs the request here and never passes through the
    // app's Axios refresh interceptor, so after an idle session every search 401s, retry included.
    const get = async path => {
      const token = await freshToken();
      if (!token) throw new Error("".concat(path, " -> no session"));
      const res = await livyFetch(config, token, {
        method: 'GET',
        path,
        signal: controller.signal
      });
      // The status travels with the error so a ceiling reads differently from an outage.
      if (!res.ok) throw Object.assign(new Error("".concat(path, " -> ").concat(res.status)), {
        status: res.status
      });
      return res.json();
    };
    const timer = setTimeout(() => {
      // A search that fails silently is indistinguishable from a search that found nothing, which
      // is exactly how a broken palette looks like an empty one. Failure gets its own state.
      const onError = e => {
        if (controller.signal.aborted || (e === null || e === void 0 ? void 0 : e.name) === 'AbortError') return;
        console.error('[Livy] search failed', e);
        setFailed((e === null || e === void 0 ? void 0 : e.status) === 429 ? 'busy' : 'unavailable');
      };

      // Every setter is guarded: abort() cannot stop a response that has already resolved, so
      // without this an earlier query's results land on top of what is now on screen.
      // Staff with no company to search inside would only ever get an empty answer back.
      const entities = needsCompany ? Promise.resolve() : get("/search?q=".concat(encodeURIComponent(query), "&scope=").concat(scope)).then(data => {
        if (controller.signal.aborted) return;
        setAppliedScope(data.scope === 'platform' ? 'platform' : 'company');
        setCutShort(truncatedSections(data.truncated));
        const _flattenSearchResults = flattenSearchResults(data.results),
          rows = _flattenSearchResults.hits,
          dropped = _flattenSearchResults.dropped;
        if (dropped) console.warn("[Livy] ".concat(dropped, " search rows had no destination and were not shown"));
        setHits(rows);
      }).catch(onError);

      // Matched server side over title and body, so an answer inside an article is findable and
      // the whole corpus no longer crosses the wire on every keystroke.
      const knowledge = get("/kb/search?q=".concat(encodeURIComponent(query))).then(data => {
        var _data$articles;
        if (controller.signal.aborted) return;
        setArticles((_data$articles = data.articles) !== null && _data$articles !== void 0 ? _data$articles : []);
      }).catch(onError);
      void Promise.all([entities, knowledge]).finally(() => {
        if (!controller.signal.aborted) setSearching(false);
      });
    }, 200);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, query, attempt, scope, needsCompany]);

  // Keep the highlighted row on screen; without this the cursor walked off the bottom of the list.
  useEffect(() => {
    var _listRef$current;
    (_listRef$current = listRef.current) === null || _listRef$current === void 0 || (_listRef$current = _listRef$current.querySelector('[aria-selected="true"]')) === null || _listRef$current === void 0 || _listRef$current.scrollIntoView({
      block: 'nearest'
    });
  }, [cursor, items.length]);
  const run = useCallback(cmd => {
    var _cmd$topic;
    if (!(cmd !== null && cmd !== void 0 && cmd.path) && !(cmd !== null && cmd !== void 0 && cmd.drawer)) return;
    rememberRecent(cmd.id);
    setRecents(readRecents());
    onClose();
    setQuery('');
    // Already on Livy's page: the launcher puts the cursor in its composer instead of a no-op navigation.
    if (cmd.id === 'livy' && /^\/livy(\/chat)?\/?$/i.test(window.location.pathname)) askLivy();else if (cmd.path) navigate(cmd.path);else if (cmd.drawer === 'ask') askLivy();else helpLivy((_cmd$topic = cmd.topic) !== null && _cmd$topic !== void 0 ? _cmd$topic : '', cmd.article);
  }, [navigate, onClose]);
  const activeId = items[cursor] ? "livy-option-".concat(cursor) : undefined;
  return /*#__PURE__*/React.createElement(Dialog, {
    open: open,
    onClose: onClose,
    fullWidth: true,
    maxWidth: "sm",
    PaperProps: {
      sx: {
        position: 'fixed',
        top: 80,
        m: 0
      }
    }
  }, /*#__PURE__*/React.createElement(Box, {
    sx: {
      px: '16px',
      py: '12px',
      borderBottom: 1,
      borderColor: 'divider'
    }
  }, /*#__PURE__*/React.createElement(InputBase, {
    autoFocus: true,
    fullWidth: true,
    value: query,
    placeholder: "Search or jump to\u2026",
    onChange: e => setQuery(e.target.value),
    onKeyDown: e => {
      // Flipping scope without leaving the input: the point of the palette is that a hand
      // never has to leave the keyboard.
      //
      // Either spelling of the key. macOS composes Option+A into 'å', so e.key alone was
      // unreachable on exactly the platform whose hint said to press it; e.code alone would
      // then miss AZERTY, where the key labelled A reports code KeyQ.
      if (canToggle && e.altKey && (e.code === 'KeyA' || e.key.toLowerCase() === 'a')) {
        e.preventDefault();
        chooseScope(scope === 'platform' ? 'company' : 'platform');
        return;
      }
      // One unified list, so keyboard focus never jumps between panes.
      // Both bounds, both ways: ArrowDown on an empty list gave Math.min(1, -1) = -1, and a
      // negative cursor stuck until some unrelated state change happened to clamp it.
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setCursor(c => clampCursor(c + 1, items.length));
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setCursor(c => clampCursor(c - 1, items.length));
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        run(items[cursor]);
      }
    },
    inputProps: {
      'aria-label': 'Command palette',
      role: 'combobox',
      // The listbox is always rendered while the dialog is open — it carries the searching and
      // empty states too — so the popup is expanded whether or not it currently holds an option.
      'aria-expanded': true,
      'aria-controls': 'livy-palette-list',
      'aria-activedescendant': activeId,
      'aria-autocomplete': 'list'
    }
  })), canWiden && /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "6px",
    alignItems: "center",
    sx: {
      px: '16px',
      py: '8px',
      borderBottom: 1,
      borderColor: 'divider',
      flexWrap: 'wrap',
      rowGap: '6px'
    }
  }, scopeOptions.map(_ref3 => {
    let _ref4 = _slicedToArray(_ref3, 2),
      value = _ref4[0],
      label = _ref4[1];
    return /*#__PURE__*/React.createElement(Chip, {
      key: value,
      size: "small"
      // The tick is the part that cannot be styled away. MUI's filled+primary treatment
      // resolved to this panel's own background, so the selected chip was invisible and
      // the control read as inert - and an inverting dark-mode extension, which several
      // people here run, flattens background and border colour to the same pair whatever
      // the theme asks for. Weight, border width and a glyph survive that; colour does not.
      ,
      label: scope === value ? "\u2713 ".concat(label) : label,
      onClick: () => chooseScope(value),
      variant: "outlined",
      "aria-pressed": scope === value,
      sx: {
        maxWidth: 240,
        bgcolor: scope === value ? 'action.selected' : 'transparent',
        borderWidth: scope === value ? 2 : 1,
        borderColor: scope === value ? 'text.primary' : 'divider',
        color: 'text.primary',
        fontWeight: scope === value ? 700 : 400,
        opacity: scope === value ? 1 : 0.7
      }
    });
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary",
    sx: {
      ml: 'auto'
    }
  }, !canToggle ? 'You are not impersonating a company' : scope === 'platform' ? "".concat(scopeShortcut, " to search ").concat(companyName, " only") : "".concat(scopeShortcut, " to search all companies"))), needsCompany && /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary",
    sx: {
      px: '16px',
      py: '8px',
      borderBottom: 1,
      borderColor: 'divider'
    }
  }, "Impersonate a company to search its properties and residents. Navigation still works."), scope === 'platform' && appliedScope === 'company' && shouldSearch(query) && !searching && /*#__PURE__*/React.createElement(Alert, {
    severity: "info",
    variant: "outlined",
    sx: {
      borderRadius: 0,
      borderLeft: 0,
      borderRight: 0
    }
  }, "This search covered ", companyName, " only."), /*#__PURE__*/React.createElement(LinearProgress, {
    sx: {
      height: 2,
      opacity: searching ? 1 : 0,
      transition: 'opacity .15s'
    },
    "aria-label": searching ? 'Searching' : undefined
  }), failed && /*#__PURE__*/React.createElement(Alert, {
    severity: "warning",
    variant: "outlined",
    sx: {
      borderRadius: 0,
      borderLeft: 0,
      borderRight: 0
    },
    action: /*#__PURE__*/React.createElement(Typography, {
      component: "button",
      onClick: () => setAttempt(a => a + 1),
      sx: {
        background: 'none',
        border: 0,
        cursor: 'pointer',
        color: 'inherit',
        font: 'inherit',
        textDecoration: 'underline'
      }
    }, "Retry")
  }, failed === 'busy' ? 'Too many searches in the last minute. Wait a moment, then retry. Navigation still works.' : 'Search is unavailable right now. Navigation still works.'), /*#__PURE__*/React.createElement(List, {
    dense: true,
    ref: listRef,
    id: "livy-palette-list",
    role: "listbox",
    "aria-label": "Search results",
    sx: {
      maxHeight: 360,
      overflowY: 'auto',
      py: 0
    }
  }, sections.map(section => /*#__PURE__*/React.createElement("li", {
    key: section.label
  }, /*#__PURE__*/React.createElement(ListSubheader, {
    component: "div",
    disableSticky: true,
    sx: {
      lineHeight: '28px',
      fontSize: 11,
      letterSpacing: '.08em',
      textTransform: 'uppercase'
    }
  }, section.label, " \xB7 ", section.items.length, cutShort.includes(section.label) ? '+' : ''), /*#__PURE__*/React.createElement("ul", {
    style: {
      padding: 0,
      margin: 0,
      listStyle: 'none'
    }
  }, section.items.map(_ref5 => {
    let cmd = _ref5.cmd,
      index = _ref5.index;
    return /*#__PURE__*/React.createElement(ListItemButton, {
      component: "li",
      key: cmd.id,
      id: "livy-option-".concat(index),
      role: "option",
      "aria-selected": index === cursor,
      selected: index === cursor,
      onClick: () => run(cmd)
    }, /*#__PURE__*/React.createElement(ListItemText, {
      primary: /*#__PURE__*/React.createElement(Highlighted, {
        label: cmd.label,
        query: query
      }),
      secondary: cmd.keywords ? "".concat(cmd.section, " \xB7 ").concat(cmd.keywords) : cmd.section
    }));
  })))), items.length === 0 && /*#__PURE__*/React.createElement(Box, {
    sx: {
      px: '16px',
      py: '16px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary"
  }, searching ? 'Searching…' : failed ? 'Navigation and actions are still listed here.' : !shouldSearch(query) ? 'Type to search.' : appliedScope === 'platform' ? 'No company, property or resident matched. Units are only searched inside a company.' : 'No matches.'))), /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "12px",
    sx: {
      px: '16px',
      py: '8px',
      borderTop: 1,
      borderColor: 'divider'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary"
  }, "\u2191\u2193 navigate"), /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary"
  }, "\u21B5 select"), /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary"
  }, "esc close")));
});

/** Ripples off inside Livy: see LivyTheme. */
export function LivyCommandPalette(props) {
  return /*#__PURE__*/React.createElement(LivyTheme, null, /*#__PURE__*/React.createElement(LivyCommandPaletteBody, props));
}